/**
 * Shared utility functions for dashboard pages.
 *
 * Extracted from AdminDashboard, MyInterviews, SessionDetail, and
 * CandidateSessionReport to avoid code duplication.
 */

export type Recommendation = 'Hire' | 'No Hire' | 'Strong Hire' | 'Hold' | null;
export type SessionStatus = 'pending' | 'in_progress' | 'completed';

/** Return pill CSS class and display text for a hiring recommendation. */
export const recPill = (rec: Recommendation) => {
  switch (rec) {
    case 'Strong Hire': return { cls: 'pill pill-success', text: 'Strong Hire' }
    case 'Hire':        return { cls: 'pill pill-success', text: 'Hire' }
    case 'Hold':        return { cls: 'pill pill-warn',    text: 'Hold' }
    case 'No Hire':     return { cls: 'pill pill-danger',  text: 'No Hire' }
    default:            return { cls: 'pill pill-muted',   text: '—' }
  }
}

/** Return pill CSS class and display text for a session status. */
export const statusPill = (status: SessionStatus) => {
  switch (status) {
    case 'completed':   return { cls: 'pill pill-success', text: 'Completed' }
    case 'in_progress': return { cls: 'pill pill-accent',  text: 'In Progress' }
    case 'pending':     return { cls: 'pill pill-muted',   text: 'Pending' }
    default:            return { cls: 'pill pill-muted',   text: status }
  }
}

/** Return badge class (AppShell world) and display text for a recommendation. */
export const recBadge = (rec: Recommendation) => {
  switch (rec) {
    case 'Strong Hire': return { cls: 'badge success', text: 'Strong Hire' }
    case 'Hire':        return { cls: 'badge success', text: 'Hire' }
    case 'Hold':        return { cls: 'badge pending', text: 'Hold' }
    case 'No Hire':     return { cls: 'badge danger',  text: 'No Hire' }
    default:            return { cls: 'badge muted',   text: '—' }
  }
}

/** Return badge class (AppShell world) and display text for a session status. */
export const statusBadge = (status: SessionStatus) => {
  switch (status) {
    case 'completed':   return { cls: 'badge success', text: 'Completed' }
    case 'in_progress': return { cls: 'badge pending', text: 'In progress' }
    case 'pending':     return { cls: 'badge pending', text: 'Pending' }
    default:            return { cls: 'badge muted',   text: status }
  }
}

/** Format an ISO date string to a human-readable date. */
export const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

/** Format an ISO datetime string, returning '—' for null. */
export const formatDateTime = (iso: string | null) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

/** True when an invite link's expiry has passed (null expiry never expires). */
export const isExpired = (expiresAt: string | null) =>
  !!expiresAt && new Date(expiresAt).getTime() <= Date.now()


/** Format seconds into a human-readable duration string. */
export const formatDuration = (secs: number | null | undefined) => {
  if (secs == null) return '—'
  if (secs < 60) return `${secs}s`
  const m = Math.floor(secs / 60)
  const s = secs % 60
  if (m < 60) return `${m}m ${s}s`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}

/**
 * Format a millisecond span as "<1m" | "38m" | "3h 20m" | "2d 4h".
 *
 * Distinct from `formatDuration` (seconds in, second resolution): spans are
 * long and read better floored to minutes, and a sub-minute same-instant claim
 * must render "<1m" rather than "0s".
 */
export const formatSpan = (ms: number): string => {
  const mins = Math.floor(ms / 60_000)
  if (mins < 1) return '<1m'
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return mins % 60 > 0 ? `${hours}h ${mins % 60}m` : `${hours}h`
  const days = Math.floor(hours / 24)
  return hours % 24 > 0 ? `${days}d ${hours % 24}h` : `${days}d`
}

/** Extract up to two initials from a name, or '?' for empty input. */
export const initials = (name: string) =>
  name.split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase() || '?'
